/**
 * Owners API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import { Owner, OwnerStatement } from '@repo/shared-types';
import type { Note } from '@repo/shared-types/common/notes';
import { apiTransport as router } from './transport';
import { getContext } from './core';

/** Body for POST /owners/statements/generate — server computes statement lines; client must not send totals. */
export type OwnerStatementGeneratePayload = {
  ownerId: string;
  period: string;
  startDate?: string;
  endDate?: string;
};

// Owner statements: list GET /owners/statements; create/generate both POST /owners/statements/generate (RPC).
export const ownersApi = {
  list: async (filters?: Record<string, string>) => {
    const context = getContext();
    return router.get<Owner[] | { data: Owner[]; total: number }>('/owners', context, Object.keys(filters ?? {}).length ? filters : undefined);
  },
  get: async (id: string) => {
    // Pass context so the controller can enforce the SMO cross-owner guard
    // (`ownerController.owners.get`). Without context the backend sees an empty
    // actor, skips the guard, and falls through to a 404 — a subtle auth
    // bypass when an SMO tries to read another owner's id.
    const context = getContext();
    return router.get<Owner>(`/owners/${id}`, context);
  },
  create: async (owner: Owner) => {
    const context = getContext();
    return router.post<Owner>('/owners', owner, context);
  },
  update: async (id: string, updates: Partial<Owner>) => {
    const context = getContext();
    return router.patch<Owner>(`/owners/${id}`, updates, context);
  },
  delete: async (id: string) => {
    const context = getContext();
    return router.delete<{ success: boolean }>(`/owners/${id}`, context);
  },
  notes: {
    list: async (ownerId: string) => router.get<Note[]>(`/owners/${ownerId}/notes`),
    create: async (note: {
      ownerId: string;
      content: string;
      category?: string;
      isImportant?: boolean;
      operatingOrganizationId?: string;
    }) => {
      const context = getContext();
      const { ownerId, ...body } = note;
      return router.post<Note>(`/owners/${ownerId}/notes`, body, context);
    },
    update: async (id: string, updates: { content: string }) => router.patch<Note>(`/owners/notes/${id}`, updates),
    delete: async (id: string) => router.delete<{ success: boolean }>(`/owners/notes/${id}`)
  },
  statements: {
    list: async () => router.get<OwnerStatement[]>('/owners/statements'),
    /** Same RPC as `generate`; alias for callers that conceptually "create" a persisted statement row. */
    create: async (payload: OwnerStatementGeneratePayload) => {
      const context = getContext();
      const ownerId = payload.ownerId.trim();
      const period = payload.period.trim();
      if (!ownerId || !period) {
        throw new Error('ownerId and period are required to generate an owner statement');
      }
      return router.post<OwnerStatement>(
        '/owners/statements/generate',
        {
          ownerId,
          period,
          ...(payload.startDate === undefined ? {} : { startDate: payload.startDate }),
          ...(payload.endDate === undefined ? {} : { endDate: payload.endDate }),
        },
        context,
      );
    },
    generate: async (ownerId: string, period: string, startDate?: string, endDate?: string) => {
      const context = getContext();
      return router.post<OwnerStatement>(
        '/owners/statements/generate',
        { ownerId, period, startDate, endDate },
        context,
      );
    },
  },
};

